import * as React from "react";
import * as H from "history";
import { match } from "react-router";
import { toast } from "react-toastify";

import {
  FilterParams,
  ITerminology,
  PaginatedParams
} from "src/library/utils";
import { Table } from "src/core/dashboard/components/widgets";
import {
  IUser,
  UpdateUserParams,
  useUserApi
} from "src/core/users/api/users.api";
import { TableRefProps } from "src/core/dashboard/components/widgets/table/Table";
import { ActionButton } from "src/library/components/Button";
import { Icons } from "src/library/components/Icon/types";
import {
  ICustomer,
  useCustomerApi
} from "src/core/customers/api/customers.api";
import { getUserAssignableCustomersTableColumns } from "src/core/shared/components/tables/userAssignableCustomersTableColumn";

import { UrlParams } from "../UserDrops/CustomerUserDropAssignmentPage";
import { SelectableCustomer } from "./UserAssignedCustomerTab";

type TabProps = {
  user: IUser;
  history: H.History;
  terminology: ITerminology;
  match: match<UrlParams> | null;
  reloadUser: () => void;
};

const defaultPagination: PaginatedParams<ICustomer> = {
  page: 0,
  limit: 20,
  offset: 0,
  sort: [ [ "accountNumber", "DESC" ] ]
};

/**
 * Assignable customers to a user tab, displayed in a table
 *
 * @returns
 */
const CustomerUserTab: React.ReactFCWithChildren<TabProps> = props => {
  const {
    terminology, user, reloadUser
  } = props;

  const TableRef = React.useRef<TableRefProps>(null);
  const [ updateUserResponse, updateUser ] = useUserApi("UPDATE_USER");
  const [ customerResponse, getCustomers ] = useCustomerApi("LIST_USER_ASSIGNABLE_CUSTOMERS");
  const [ availableCustomers, setAvailableCustomers ] = React.useState<SelectableCustomer[]>([]);
  const [ reloadUserFlag, setReloadUserFlag ] = React.useState(false);
  const [ loading, setLoading ] = React.useState(false);

  //getting the customers out of the response and mapping them to have a selected property
  React.useEffect(() => {
    setAvailableCustomers(() => {
      const customers = customerResponse.data?.result ? customerResponse.data?.result as SelectableCustomer[] : [];

      return customers.map(customer => ({
        ...customer,
        selected: false
      }));
    });
  }, [ customerResponse.data?.result ]);

  //loading is set true by buttons too, this is so there is an instant reaction to interactions.
  React.useEffect(() => {
    if (customerResponse.pending) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [ customerResponse ]);

  //if any errors
  React.useEffect(() => {
    if (updateUserResponse.error) {
      toast.error(`Could not save ${terminology.user.singular.text}. Please try again.`);
    }

    if (customerResponse.error) {
      toast.error(`Could not retrieve ${terminology.customer.plural.text}. Please try again`);
    }
  }, [
    terminology,
    updateUserResponse.error,
    customerResponse.error
  ]);

  const onFetchData = React.useCallback(
    (params: {filters: FilterParams<ICustomer>, pagination: PaginatedParams<ICustomer>}) => {
      getCustomers({
        userId: user.id,
        pagination: params.pagination,
        filters: params.filters
      });
    },
    [ getCustomers, user ]
  );

  React.useEffect(() => {
    if (updateUserResponse.complete) {
      if (reloadUserFlag) { //only reload user if we said to
        reloadUser();
        setReloadUserFlag(false);
      }
    }
    //only want to trigger this when the response is updated not the flag
  }, [
    reloadUser,
    updateUserResponse,
    reloadUserFlag
  ]);

  const handleUpdateUser = React.useCallback(() => {
    setAvailableCustomers(prevCustomers => {
      if (!user || !user.userCustomers) {
        return prevCustomers;
      }

      if (prevCustomers.length < 1) {
        toast.error(`Please select at least 1 ${terminology.customer.singular.text}`);

        return prevCustomers;
      }

      const customersToAssign = prevCustomers.filter(customer => customer.selected === true);

      updateUser({
        ...user,
        userCustomers: [ ...customersToAssign, ...user.userCustomers ]
      } as UpdateUserParams);
      setReloadUserFlag(true);
      setLoading(true);

      return prevCustomers.map(customer => ({
        ...customer,
        selected: false
      }));
    });
    // Return if there are no user.userBranches
  }, [
    user,
    updateUser,
    terminology
  ]);

  const columns = React.useMemo(() => {
    let isCustomerSelected = false;

    return getUserAssignableCustomersTableColumns({
      checkboxAction: (customer: ICustomer) => {
        setAvailableCustomers(prevCustomers => {
          const customerExists = prevCustomers.findIndex(selCustomer => selCustomer.id === customer.id);
          const newCustomers = [ ...prevCustomers ];

          newCustomers[ customerExists ].selected = !newCustomers[ customerExists ].selected;
          isCustomerSelected = newCustomers.filter(customer => customer.selected).length ? true : false;

          return newCustomers;
        });
      },
      bulkActionButton: () => {
        return (
          <ActionButton
            title="Bulk Actions"
            items={[
              {
                title: "Give Access",
                icon: Icons.TICK,
                disabled: !isCustomerSelected,
                action: handleUpdateUser
              },
              {
                title: "Select All",
                action: () => {
                  setAvailableCustomers(prevCustomers => {
                    const selectedCustomers = prevCustomers.filter(customer => customer.selected === true);

                    if (selectedCustomers.length === prevCustomers.length) {
                      isCustomerSelected = false;

                      return prevCustomers.map(customer => ({
                        ...customer,
                        selected: false
                      }));
                    }

                    isCustomerSelected = true;

                    return prevCustomers.map(customer => ({
                      ...customer,
                      selected: true
                    }));
                  });
                }
              }
            ]}
          />
        );
      },
      terminology
    });
  }, [ terminology, handleUpdateUser ]);

  return (
    <Table
      ref={TableRef}
      tableOf={`${user.firstName} can not access the following ${terminology.customer.plural.text}`}
      error={customerResponse.error}
      loading={loading}
      data={customerResponse.data?.result && !loading ? availableCustomers : []}
      onFetchData={onFetchData}
      defaultPagination={defaultPagination}
      pageCount={customerResponse.data?.pages ? customerResponse.data.pages : 0}
      rowCount={customerResponse.data?.count ? customerResponse.data.count : 0}
      columns={columns}
    />
  );
};

export default CustomerUserTab;
